import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  DollarSign,
  Briefcase,
  LogOut,
  Bell,
  Plus,
  Check,
  X,
  Star,
  Edit2,
  Trash2,
  Eye,
  EyeOff,
} from "lucide-react";

import useCategories from "../../hooks/useCategories";
import { useAuth } from "../../contexts/AuthContext";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Button } from "../ui/Button";
import { db, auth } from "../../firebase";

import {
  addDoc,
  collection,
  doc,
  getDocs,
  limit,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";

/* ---------------------------------------------
   Helpers
---------------------------------------------- */
function safeNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function isFiniteLatLng(location) {
  return (
    Number.isFinite(Number(location?.lat)) && Number.isFinite(Number(location?.lng))
  );
}

function haversineDistanceKm(a, b) {
  if (!isFiniteLatLng(a) || !isFiniteLatLng(b)) return Infinity;

  const lat1 = Number(a.lat);
  const lng1 = Number(a.lng);
  const lat2 = Number(b.lat);
  const lng2 = Number(b.lng);

  const toRad = (deg) => (deg * Math.PI) / 180;
  const R = 6371;

  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);

  const aa =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(aa), Math.sqrt(1 - aa));
  return R * c;
}

function formatPreferredDate(value) {
  if (!value) return "Unknown date";

  if (typeof value?.toDate === "function") {
    return value.toDate().toLocaleDateString();
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown date";

  return date.toLocaleDateString();
}

function getTaskerProfile(profile) {
  if (profile?.roleProfile) return profile.roleProfile;
  return profile || {};
}

function getJobStatusLabel(status) {
  if (status === "assigned") return "In Progress";
  if (status === "completed") return "Completed";
  if (status === "cancelled") return "Cancelled";
  return status || "Unknown";
}

function getUpcomingStatusClasses(status) {
  if (status === "assigned") {
    return "bg-yellow-100 text-yellow-800";
  }
  if (status === "completed") {
    return "bg-green-100 text-green-800";
  }
  if (status === "cancelled") {
    return "bg-red-100 text-red-800";
  }
  return "bg-gray-100 text-gray-700";
}

/* ---------------------------------------------
   Helper: Sync taskers.categories/subcategories
   from ACTIVE offerings only
---------------------------------------------- */
export async function syncTaskerTaxonomyFromActiveOfferings(db, taskerId) {
  if (!taskerId) return;

  const q = query(
    collection(db, "serviceOfferings"),
    where("taskerId", "==", taskerId),
    limit(300)
  );

  const snap = await getDocs(q);
  const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

  const active = rows.filter((r) => (r?.status || "active") === "active");

  const categoriesSet = new Set();
  const subcategoriesSet = new Set();

  for (const r of active) {
    if (r?.categoryId) categoriesSet.add(r.categoryId);
    for (const s of r?.subcategoryIds || []) {
      if (s) subcategoriesSet.add(s);
    }
  }

  await updateDoc(doc(db, "taskers", taskerId), {
    categories: Array.from(categoriesSet),
    subcategories: Array.from(subcategoriesSet),
    updatedAt: serverTimestamp(),
  });
}

export default function TaskerDashboard({ onLogout }) {
  const navigate = useNavigate();
  const { categories } = useCategories();
  const { profile } = useAuth();

  const taskerProfile = getTaskerProfile(profile);
  const taskerId = profile?.uid || auth?.currentUser?.uid || null;
  const taskerLocation = taskerProfile?.location || null;
  const serviceRadiusKm = safeNumber(taskerProfile?.serviceRadiusKm, 0);

  const [myServiceOfferings, setMyServiceOfferings] = useState([]);
  const [offeringsLoading, setOfferingsLoading] = useState(true);

  const [directJobRequests, setDirectJobRequests] = useState([]);
  const [directJobRequestsLoading, setDirectJobRequestsLoading] = useState(true);

  const [nearbyServiceRequests, setNearbyServiceRequests] = useState([]);
  const [nearbyServiceRequestsLoading, setNearbyServiceRequestsLoading] = useState(true);

  const [mySentOfferRequestIds, setMySentOfferRequestIds] = useState([]);
  const [mySentOffersLoading, setMySentOffersLoading] = useState(true);

  const [upcomingJobs, setUpcomingJobs] = useState([]);
  const [upcomingJobsLoading, setUpcomingJobsLoading] = useState(true);

  useEffect(() => {
    if (!taskerId) {
      setMyServiceOfferings([]);
      setOfferingsLoading(false);
      return;
    }

    setOfferingsLoading(true);

    const q = query(
      collection(db, "serviceOfferings"),
      where("taskerId", "==", taskerId),
      limit(100)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        const visible = rows.filter((r) => (r?.status || "active") !== "deleted");

        visible.sort((a, b) => {
          const sa = a?.status === "active" ? 0 : 1;
          const sb = b?.status === "active" ? 0 : 1;
          if (sa !== sb) return sa - sb;

          const ta = a?.updatedAt?.seconds || a?.createdAt?.seconds || 0;
          const tb = b?.updatedAt?.seconds || b?.createdAt?.seconds || 0;
          return tb - ta;
        });

        setMyServiceOfferings(visible);
        setOfferingsLoading(false);
      },
      (err) => {
        console.error(err);
        setMyServiceOfferings([]);
        setOfferingsLoading(false);
      }
    );

    return () => unsub();
  }, [taskerId]);

  useEffect(() => {
    if (!taskerId) {
      setDirectJobRequests([]);
      setDirectJobRequestsLoading(false);
      return;
    }

    setDirectJobRequestsLoading(true);

    const q = query(
      collection(db, "jobs"),
      where("taskerId", "==", taskerId),
      where("status", "==", "open"),
      limit(100)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        rows.sort((a, b) => {
          const ta = a?.updatedAt?.seconds || a?.createdAt?.seconds || 0;
          const tb = b?.updatedAt?.seconds || b?.createdAt?.seconds || 0;
          return tb - ta;
        });

        setDirectJobRequests(rows);
        setDirectJobRequestsLoading(false);
      },
      (err) => {
        console.error(err);
        setDirectJobRequests([]);
        setDirectJobRequestsLoading(false);
      }
    );

    return () => unsub();
  }, [taskerId]);

  useEffect(() => {
    if (!taskerId) {
      setMySentOfferRequestIds([]);
      setMySentOffersLoading(false);
      return;
    }

    setMySentOffersLoading(true);

    const q = query(
      collection(db, "taskerOffers"),
      where("taskerId", "==", taskerId),
      limit(200)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const requestIds = snap.docs
          .map((d) => d.data()?.requestedId)
          .filter(Boolean);

        setMySentOfferRequestIds(requestIds);
        setMySentOffersLoading(false);
      },
      (err) => {
        console.error(err);
        setMySentOfferRequestIds([]);
        setMySentOffersLoading(false);
      }
    );

    return () => unsub();
  }, [taskerId]);

  useEffect(() => {
    if (!taskerId) {
      setUpcomingJobs([]);
      setUpcomingJobsLoading(false);
      return;
    }

    setUpcomingJobsLoading(true);

    const q = query(
      collection(db, "jobs"),
      where("taskerId", "==", taskerId),
      where("status", "in", ["assigned", "completed", "cancelled"]),
      limit(100)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        rows.sort((a, b) => {
          const ta = a?.updatedAt?.seconds || a?.createdAt?.seconds || 0;
          const tb = b?.updatedAt?.seconds || b?.createdAt?.seconds || 0;
          return tb - ta;
        });

        setUpcomingJobs(rows);
        setUpcomingJobsLoading(false);
      },
      (err) => {
        console.error(err);
        setUpcomingJobs([]);
        setUpcomingJobsLoading(false);
      }
    );

    return () => unsub();
  }, [taskerId]);

  useEffect(() => {
    if (!taskerId) {
      setNearbyServiceRequests([]);
      setNearbyServiceRequestsLoading(false);
      return;
    }

    setNearbyServiceRequestsLoading(true);

    const q = query(
      collection(db, "serviceRequests"),
      where("status", "==", "open"),
      limit(150)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const offeredRequestIdsSet = new Set(mySentOfferRequestIds);

        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        const filtered = rows.filter((r) => {
          if (offeredRequestIdsSet.has(r.id)) return false;
          if (!isFiniteLatLng(taskerLocation)) return false;
          if (!isFiniteLatLng(r?.location)) return false;

          const distanceKm = haversineDistanceKm(taskerLocation, r.location);
          return distanceKm <= serviceRadiusKm;
        });

        filtered.sort((a, b) => {
          const ta = a?.updatedAt?.seconds || a?.createdAt?.seconds || 0;
          const tb = b?.updatedAt?.seconds || b?.createdAt?.seconds || 0;
          return tb - ta;
        });

        setNearbyServiceRequests(filtered);
        setNearbyServiceRequestsLoading(false);
      },
      (err) => {
        console.error(err);
        setNearbyServiceRequests([]);
        setNearbyServiceRequestsLoading(false);
      }
    );

    return () => unsub();
  }, [taskerId, taskerLocation, serviceRadiusKm, mySentOfferRequestIds]);

  const handleEditOffering = (offeringId) => {
    navigate(`/tasker/service-offerings/${offeringId}/edit`);
  };

  const handleToggleOfferingStatus = async (offering) => {
    if (!taskerId || !offering?.id) return;

    const current = offering?.status || "active";
    const nextStatus = current === "active" ? "in-active" : "active";

    try {
      await updateDoc(doc(db, "serviceOfferings", offering.id), {
        status: nextStatus,
        updatedAt: serverTimestamp(),
      });

      await syncTaskerTaxonomyFromActiveOfferings(db, taskerId);
    } catch (e) {
      console.error(e);
    }
  };

  const handleDeleteOffering = async (offering) => {
    if (!taskerId || !offering?.id) return;

    const ok = window.confirm("Delete this service offering?");
    if (!ok) return;

    try {
      await updateDoc(doc(db, "serviceOfferings", offering.id), {
        status: "deleted",
        deletedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      await syncTaskerTaxonomyFromActiveOfferings(db, taskerId);
    } catch (e) {
      console.error(e);
    }
  };

  const handleAcceptJob = async (job) => {
    if (!job?.id) return;

    try {
      await updateDoc(doc(db, "jobs", job.id), {
        status: "assigned",
        progressStatus: "confirmed",
        updatedAt: serverTimestamp(),
      });
    } catch (e) {
      console.error(e);
    }
  };

  const handleDeclineJob = async (job) => {
    if (!job?.id) return;

    try {
      await updateDoc(doc(db, "jobs", job.id), {
        status: "declined",
        updatedAt: serverTimestamp(),
      });
    } catch (e) {
      console.error(e);
    }
  };

  const handleSendOffer = (request) => {
    if (!request?.id) return;
    navigate(`/tasker/send-offer/${request.id}`);
  };

  const notificationCount = directJobRequests.length + nearbyServiceRequests.length;
  const isNearbySectionLoading = nearbyServiceRequestsLoading || mySentOffersLoading;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-[#006C35] rounded-lg flex items-center justify-center">
                <span className="text-white">🔧</span>
              </div>
              <span className="text-[#006C35]">TaskerConnect</span>
            </div>

            <div className="flex items-center gap-4">
              <button className="relative" type="button">
                <Bell size={24} className="text-gray-600" />
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                  {notificationCount}
                </span>
              </button>

              <button
                type="button"
                onClick={onLogout}
                className="text-gray-600 hover:text-gray-900"
              >
                <LogOut size={24} />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-start gap-4">
              <img
                src={taskerProfile?.imageUrl || "/default-avatar.png"}
                alt={taskerProfile?.name || "Unnamed Tasker"}
                className="w-20 h-20 rounded-xl object-cover"
              />
              <div>
                <h2 className="text-[#1E3A8A] mb-2">
                  {taskerProfile?.name || "Unnamed Tasker"}
                </h2>

                <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                  <div className="flex items-center gap-1">
                    <Star size={16} className="text-yellow-500 fill-yellow-500" />
                    <span className="text-[#1E3A8A]">
                      {taskerProfile?.ratingSummary?.avg || 0}
                    </span>
                    <span className="text-gray-500">
                      ({taskerProfile?.ratingSummary?.count || 0} reviews)
                    </span>
                  </div>

                  <div className="flex items-center gap-1">
                    <Briefcase size={16} className="text-[#006C35]" />
                    <span>{taskerProfile?.completedJobs || 0} jobs completed</span>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-sm">
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full">
                    {taskerProfile?.availability || "Unknown"}
                  </span>
                  <span className="text-gray-600">
                    Response: {taskerProfile?.responseTime || "N/A"}
                  </span>
                </div>
              </div>
            </div>

            <Link
              to="/tasker/edit-profile"
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Edit2 size={16} />
              Edit Profile
            </Link>
          </div>

          <div className="border-t border-gray-200 pt-4 mb-4">
            {taskerProfile?.bio ? (
              <p className="text-gray-700 mb-4">{taskerProfile.bio}</p>
            ) : (
              <p className="text-gray-400 mb-4">No bio provided.</p>
            )}

            <div className="mb-4">
              <h4 className="text-sm text-gray-600 mb-2">Service Categories</h4>
              <div className="flex flex-wrap gap-2">
                {(taskerProfile?.categories || []).map((catId) => {
                  const category = categories.find((c) => c.id === catId);
                  return category ? (
                    <span
                      key={catId}
                      className="px-3 py-1 bg-blue-50 text-blue-700 rounded-lg text-sm"
                    >
                      {category.icon} {category.name}
                    </span>
                  ) : null;
                })}
              </div>
            </div>

            <div>
              <h4 className="text-sm text-gray-600 mb-2">Skills & Specializations</h4>
              <div className="flex flex-wrap gap-2">
                {(taskerProfile?.subcategories || []).map((subId) => {
                  for (const category of categories) {
                    const subcategory = (category.subcategories || []).find(
                      (sub) => sub.id === subId
                    );
                    if (subcategory) {
                      return (
                        <span
                          key={subId}
                          className="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg text-sm"
                        >
                          {subcategory.name}
                        </span>
                      );
                    }
                  }
                  return null;
                })}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-200">
            <div className="text-center">
              <div className="text-2xl text-[#1E3A8A] mb-1">
                {taskerProfile?.completedJobs || 0}
              </div>
              <div className="text-sm text-gray-600">Jobs Completed</div>
            </div>

            <div className="text-center">
              <div className="text-2xl text-[#1E3A8A] mb-1">
                {taskerProfile?.ratingSummary?.avg || 0}
              </div>
              <div className="text-sm text-gray-600">Average Rating</div>
            </div>

            <div className="text-center">
              <div className="text-2xl text-[#1E3A8A] mb-1">
                {taskerProfile?.priceRange
                  ? `${taskerProfile.priceRange.min ?? 0}-${taskerProfile.priceRange.max ?? 0} SAR`
                  : "0-0 SAR"}
              </div>
              <div className="text-sm text-gray-600">Price Range</div>
            </div>
          </div>
        </div>

        <div className="bg-linear-to-r from-[#006C35] to-[#005428] rounded-2xl p-6 text-white mb-6">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign size={24} />
            <span className="opacity-90">Earnings This Month</span>
          </div>
          <div className="text-4xl mb-4">4,200 SAR</div>
          <Link
            to="/tasker/earnings"
            className="inline-block bg-white text-[#006C35] px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            View Details
          </Link>
        </div>

        <Tabs defaultValue="requests" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="requests">
              Job Requests ({notificationCount})
            </TabsTrigger>
            <TabsTrigger value="services">
              My Services ({myServiceOfferings.length})
            </TabsTrigger>
            <TabsTrigger value="upcoming">
              Upcoming ({upcomingJobs.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="requests">
            <div className="space-y-4">
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="text-[#1E3A8A] mb-4">Direct Job Requests</h3>

                {directJobRequestsLoading ? (
                  <div className="py-8 text-center text-gray-500">Loading direct jobs...</div>
                ) : directJobRequests.length > 0 ? (
                  <div className="space-y-3">
                    {directJobRequests.map((job) => (
                      <JobRequestCard
                        key={job.id}
                        job={job}
                        onAccept={() => handleAcceptJob(job)}
                        onDecline={() => handleDeclineJob(job)}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="py-4 text-gray-500">No direct job requests at the moment.</div>
                )}
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="text-[#1E3A8A] mb-4">Open Service Requests Nearby</h3>

                {!isFiniteLatLng(taskerLocation) ? (
                  <div className="py-4 text-gray-500">
                    Add your tasker location to see nearby service requests.
                  </div>
                ) : isNearbySectionLoading ? (
                  <div className="py-8 text-center text-gray-500">
                    Loading nearby service requests...
                  </div>
                ) : nearbyServiceRequests.length > 0 ? (
                  <div className="space-y-3">
                    {nearbyServiceRequests.map((request) => (
                      <ServiceRequestCard
                        key={request.id}
                        request={request}
                        taskerLocation={taskerLocation}
                        onSendOffer={() => handleSendOffer(request)}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="py-4 text-gray-500">
                    No nearby service requests within {serviceRadiusKm} km.
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="services">
            <div className="space-y-6">
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-[#1E3A8A]">Service Listings</h3>
                  <span className="text-sm text-gray-500">
                    {offeringsLoading ? "Loading..." : `${myServiceOfferings.length} total`}
                  </span>
                </div>

                {offeringsLoading ? (
                  <div className="py-10 text-center text-gray-500">Loading services...</div>
                ) : myServiceOfferings.length === 0 ? (
                  <div className="py-10 text-center text-gray-500">No services yet.</div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {myServiceOfferings.map((offering) => (
                      <ServiceOfferingCard
                        key={offering.id}
                        offering={offering}
                        onEdit={() => handleEditOffering(offering.id)}
                        onToggle={() => handleToggleOfferingStatus(offering)}
                        onDelete={() => handleDeleteOffering(offering)}
                      />
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-[#1E3A8A]">Create New Service Offering</h3>
                  <Button type="button" className="bg-[#006C35] hover:bg-[#005428]">
                    <Plus size={20} className="mr-2" />
                    Quick Add
                  </Button>
                </div>

                <CreateServiceOffering taskerId={taskerId} />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="upcoming">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="text-[#1E3A8A] mb-4">Upcoming Jobs</h3>

              <div className="space-y-3">
                {upcomingJobsLoading ? (
                  <p className="text-gray-500 text-center py-8">Loading upcoming jobs...</p>
                ) : upcomingJobs.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No upcoming jobs</p>
                ) : (
                  upcomingJobs.map((job) => (
                    <div key={job.id} className="p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <button
                            type="button"
                            onClick={() => navigate(`/tasker/job-progress/${job.id}`)}
                            className="text-[#1E3A8A] mb-1 hover:underline text-left"
                          >
                            {job.subcategoryId || "Untitled Job"}
                          </button>
                          <p className="text-sm text-gray-600">
                            Customer: {job.customerName || "Unknown Customer"}
                          </p>
                        </div>

                        <span
                          className={`text-sm px-3 py-1 rounded-full ${getUpcomingStatusClasses(
                            job.status
                          )}`}
                        >
                          {getJobStatusLabel(job.status)}
                        </span>
                      </div>

                      <p className="text-sm text-gray-600 mb-2">
                        {job.note || "No description provided."}
                      </p>

                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                        <span>📍 {job.locationText || "Unknown location"}</span>
                        <span>📅 {formatPreferredDate(job.preferredDate)}</span>
                        <span>⏰ {job.preferredTime || "Unknown time"}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function ServiceOfferingCard({ offering, onEdit, onToggle, onDelete }) {
  const { categories } = useCategories();

  const category = categories?.find((c) => c.id === offering?.categoryId);

  const subcats = (offering?.subcategoryIds || [])
    .map((subId) => {
      for (const c of categories || []) {
        const s = (c.subcategories || []).find((x) => x.id === subId);
        if (s) return s;
      }
      return null;
    })
    .filter(Boolean);

  const isActive = (offering?.status || "active") === "active";

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden hover:border-[#006C35] transition-all bg-white">
      <div className="p-4">
        <h4 className="text-[#1E3A8A] text-sm mb-2">
          {offering?.title || "Untitled Service"}
        </h4>

        <div className="space-y-2 mb-3">
          {category ? (
            <div className="text-xs text-gray-500">
              {category.icon} {category.name}
            </div>
          ) : (
            <div className="text-xs text-gray-400">No category</div>
          )}

          {subcats.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {subcats.map((s) => (
                <span
                  key={s.id}
                  className="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg text-xs"
                >
                  {s.name}
                </span>
              ))}
            </div>
          )}
        </div>

        {offering?.description ? (
          <p className="text-sm text-gray-600 mb-3 line-clamp-2">{offering.description}</p>
        ) : (
          <p className="text-sm text-gray-400 mb-3">No description provided.</p>
        )}

        <div className="flex items-center justify-between">
          <div className="text-sm text-[#006C35]">
            Price: {typeof offering?.price === "number" ? offering.price : 0} SAR
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={onToggle}
              className={`p-2 rounded ${
                isActive ? "text-blue-600 hover:bg-blue-50" : "text-gray-500 hover:bg-gray-50"
              }`}
              title={isActive ? "Turn Off (In-active)" : "Turn On (Active)"}
            >
              {isActive ? <Eye size={18} /> : <EyeOff size={18} />}
            </button>

            <button
              type="button"
              onClick={onEdit}
              className="p-2 text-gray-600 hover:bg-gray-50 rounded"
              title="Edit"
            >
              <Edit2 size={18} />
            </button>

            <button
              type="button"
              onClick={onDelete}
              className="p-2 text-red-600 hover:bg-red-50 rounded"
              title="Delete"
            >
              <Trash2 size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function JobRequestCard({ job, onAccept, onDecline }) {
  return (
    <div className="p-4 border border-gray-200 rounded-lg hover:border-[#006C35] transition-all bg-white">
      <div className="flex items-start justify-between mb-2">
        <div>
          <h4 className="text-[#1E3A8A] mb-1">{job.subcategoryId || "Untitled Job"}</h4>
          <p className="text-sm text-gray-600">
            Customer: {job.customerName || "Unknown Customer"}
          </p>
        </div>
        <span className="text-[#006C35]">{safeNumber(job.price, 0)} SAR</span>
      </div>

      <p className="text-sm text-gray-600 mb-3">{job.note || "No description provided."}</p>

      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-3">
        <span>📍 {job.locationText || "Unknown location"}</span>
        <span>📅 {formatPreferredDate(job.preferredDate)}</span>
        <span>⏰ {job.preferredTime || "Unknown time"}</span>
      </div>

      <div className="flex gap-2">
        <Button
          type="button"
          onClick={onAccept}
          className="flex-1 bg-[#006C35] hover:bg-[#005428]"
        >
          <Check size={16} className="mr-2" />
          Accept
        </Button>

        <Button
          type="button"
          onClick={onDecline}
          variant="outline"
          className="flex-1 border-red-500 text-red-500 hover:bg-red-50"
        >
          <X size={16} className="mr-2" />
          Decline
        </Button>
      </div>
    </div>
  );
}

function ServiceRequestCard({ request, taskerLocation, onSendOffer }) {
  const { categories } = useCategories();
  const category = categories?.find((c) => c.id === request.categoryId);

  const distance = haversineDistanceKm(taskerLocation, request?.location);
  const distanceLabel = Number.isFinite(distance) ? `${distance.toFixed(1)} km away` : null;

  return (
    <div className="p-4 border border-gray-200 rounded-lg hover:border-[#006C35] transition-all bg-white">
      <div className="flex items-start justify-between mb-2">
        <div>
          <h4 className="text-[#1E3A8A] mb-1">{request.title || "Untitled Request"}</h4>
          <p className="text-sm text-gray-600">
            Posted by: {request.customerName || "Customer"}
          </p>
        </div>

        <div className="text-right">
          <span className="text-[#006C35]">Budget: {safeNumber(request.budget, 0)} SAR</span>
          {category && (
            <p className="text-xs text-gray-500">
              {category.icon} {category.name}
            </p>
          )}
        </div>
      </div>

      <p className="text-sm text-gray-600 mb-3">
        {request.description || "No description provided."}
      </p>

      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-3">
        <span>📍 {request.locationText || "Unknown location"}</span>
        <span>📅 {formatPreferredDate(request.preferredDate)}</span>
        <span>⏰ {request.preferredTime || "Unknown time"}</span>
        {distanceLabel && <span>📏 {distanceLabel}</span>}
      </div>

      <Button
        type="button"
        onClick={onSendOffer}
        className="w-full bg-[#006C35] hover:bg-[#005428]"
      >
        Send Offer
      </Button>
    </div>
  );
}

/* ----------------------------
   Create Service Offering (Firestore)
---------------------------- */
function CreateServiceOffering({ taskerId }) {
  const { categories } = useCategories();

  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSubcategories, setSelectedSubcategories] = useState([]);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const category = useMemo(
    () => (categories || []).find((c) => c.id === selectedCategory),
    [categories, selectedCategory]
  );

  const toggleSubcategory = (subcatId) => {
    setSelectedSubcategories((prev) =>
      prev.includes(subcatId) ? prev.filter((id) => id !== subcatId) : [...prev, subcatId]
    );
  };

  const showToast = (type, message) => {
    setToast({ type, message });
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => setToast(null), 2500);
  };

  const validate = () => {
    const next = {};
    if (!selectedCategory) next.category = "The Service Category is mandatory";
    if (!selectedSubcategories.length) {
      next.subcategories = "The Services You Offer is mandatory";
    }
    if (!title.trim()) next.title = "The Service Title is mandatory";
    if (price === "" || Number.isNaN(Number(price)) || Number(price) <= 0) {
      next.price = "The Fixed Price (SAR) is mandatory";
    }
    return next;
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!taskerId) {
      showToast("error", "You must be logged in as a tasker.");
      return;
    }

    const nextErrors = validate();
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length) return;

    setSaving(true);

    try {
      await addDoc(collection(db, "serviceOfferings"), {
        taskerId,
        categoryId: selectedCategory,
        subcategoryIds: selectedSubcategories,
        title: title.trim(),
        description: description?.trim() || "",
        price: Number(price),
        status: "active",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      await syncTaskerTaxonomyFromActiveOfferings(db, taskerId);

      setSelectedCategory("");
      setSelectedSubcategories([]);
      setTitle("");
      setDescription("");
      setPrice("");
      setErrors({});
      showToast("success", "Service offering created successfully.");
    } catch (err) {
      console.error(err);
      showToast("error", "Failed to create service offering.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {toast && (
        <div
          className={`rounded-lg px-4 py-3 text-sm border ${
            toast.type === "success"
              ? "bg-green-50 border-green-200 text-green-700"
              : "bg-red-50 border-red-200 text-red-700"
          }`}
        >
          {toast.message}
        </div>
      )}

      <form className="space-y-6" onSubmit={onSubmit}>
        <div>
          <label className="block text-sm mb-2 text-gray-700">Service Category</label>
          <select
            value={selectedCategory}
            onChange={(e) => {
              setSelectedCategory(e.target.value);
              setSelectedSubcategories([]);
              setErrors((p) => ({ ...p, category: undefined, subcategories: undefined }));
            }}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006C35]"
          >
            <option value="">Select a category...</option>
            {(categories || []).map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.icon} {cat.name}
              </option>
            ))}
          </select>
          {errors.category && <p className="mt-1 text-sm text-red-600">{errors.category}</p>}
        </div>

        {selectedCategory && category && (
          <div>
            <label className="block text-sm mb-2 text-gray-700">
              Services You Offer (Select all that apply)
            </label>

            <div className="grid md:grid-cols-2 gap-3">
              {(category.subcategories || []).map((subcat) => (
                <label
                  key={subcat.id}
                  className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-all ${
                    selectedSubcategories.includes(subcat.id)
                      ? "border-[#006C35] bg-green-50"
                      : "border-gray-300 hover:border-gray-400"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedSubcategories.includes(subcat.id)}
                    onChange={() => {
                      toggleSubcategory(subcat.id);
                      setErrors((p) => ({ ...p, subcategories: undefined }));
                    }}
                    className="mt-1"
                  />
                  <div>
                    <div className="text-sm">{subcat.name}</div>
                    <div className="text-xs text-gray-500">{subcat.description}</div>
                  </div>
                </label>
              ))}
            </div>

            {errors.subcategories && (
              <p className="mt-1 text-sm text-red-600">{errors.subcategories}</p>
            )}
          </div>
        )}

        <div>
          <label className="block text-sm mb-2 text-gray-700">Service Title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setErrors((p) => ({ ...p, title: undefined }));
            }}
            placeholder="e.g., Professional Plumbing Services"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006C35]"
          />
          {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
        </div>

        <div>
          <label className="block text-sm mb-2 text-gray-700">Description (Optional)</label>
          <textarea
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe your service, experience, and what makes you stand out..."
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006C35]"
          />
        </div>

        <div>
          <label className="block text-sm mb-2 text-gray-700">Fixed Price (SAR)</label>
          <input
            type="number"
            value={price}
            onChange={(e) => {
              setPrice(e.target.value);
              setErrors((p) => ({ ...p, price: undefined }));
            }}
            placeholder="e.g., 250"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006C35]"
          />
          {errors.price && <p className="mt-1 text-sm text-red-600">{errors.price}</p>}
        </div>

        <Button
          type="submit"
          disabled={saving}
          className="w-full bg-[#006C35] hover:bg-[#005428] text-white py-6 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <Plus size={20} className="mr-2" />
          {saving ? "Creating..." : "Create Service Offering"}
        </Button>
      </form>
    </div>
  );
}